import React, { Component, ReactNode } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Slide } from '@mui/material';
import { WithTranslation, withTranslation } from 'react-i18next';
import { TransitionProps } from '@mui/material/transitions';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface ErrorBoundaryProps extends WithTranslation {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  showDetails: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      showDetails: false
    };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  toggleDetails = () => {
    this.setState((prevState) => ({ showDetails: !prevState.showDetails }));
  };

  render() {
    const { t } = this.props;
    const { hasError, error, showDetails } = this.state;

    if (hasError) {
      return (
        <Dialog open TransitionComponent={Transition} fullWidth maxWidth="sm">
          <DialogTitle>{t('app.error.title', { ns: 'app' })}</DialogTitle>
          <DialogContent>
            <p>{t('app.error.message', { ns: 'app' })}</p>
            {showDetails && error && (
              <pre
                style={{
                  backgroundColor: '#f4f4f4',
                  padding: '10px',
                  borderRadius: '4px',
                  overflowX: 'auto',
                  marginTop: '10px',
                  fontSize: '0.9rem'
                }}
              >
                {error.stack || error.message}
              </pre>
            )}
          </DialogContent>
          <DialogActions
            sx={{
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: { xs: 'center', md: 'flex-end' },
              gap: { xs: '8px', md: '0' }
            }}
          >
            <Button fullWidth onClick={this.toggleDetails} color="secondary" variant="outlined">
              {showDetails
                ? t('app.error.hide-details-button', { ns: 'app' })
                : t('app.error.show-details-button', { ns: 'app' })}
            </Button>
            <Button
              fullWidth
              onClick={this.handleReload}
              color="primary"
              variant="contained"
              sx={{ ml: { xs: '0 !important', md: '8px' } }}
            >
              {t('app.error.reload-button', { ns: 'app' })}
            </Button>
          </DialogActions>
        </Dialog>
      );
    }

    return this.props.children;
  }
}

const TranslatedErrorBoundary = withTranslation('app')(ErrorBoundary);
export default TranslatedErrorBoundary;
